import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError, fileUrl } from "@/lib/apiClient";
import { toast } from "sonner";
import { Wrench, MapPin, Hash, CheckCircle2, XCircle, Clock, Plus, Trash2, ShieldCheck, Star, FileText } from "lucide-react";

function RegionsEditor({ cities, setCities, ranges, setRanges }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Cidades atendidas</Label>
        {cities.map((c, i) => (
          <div key={i} className="flex gap-2 mt-1.5" data-testid={`city-row-${i}`}>
            <Input placeholder="Cidade" value={c.city} onChange={(e) => setCities(cities.map((x, idx) => idx === i ? { ...x, city: e.target.value } : x))} data-testid={`city-input-${i}`} />
            <Input placeholder="UF" maxLength={2} className="w-16" value={c.uf} onChange={(e) => setCities(cities.map((x, idx) => idx === i ? { ...x, uf: e.target.value.toUpperCase() } : x))} data-testid={`uf-input-${i}`} />
            <button type="button" className="text-slate-400 hover:text-red-500" onClick={() => setCities(cities.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" className="mt-2 gap-1" onClick={() => setCities([...cities, { city: "", uf: "" }])} data-testid="add-city-button"><Plus className="h-3.5 w-3.5" /> Cidade</Button>
      </div>
      <div>
        <Label className="text-xs">Faixas de CEP</Label>
        {ranges.map((r, i) => (
          <div key={i} className="flex gap-2 mt-1.5" data-testid={`range-row-${i}`}>
            <Input placeholder="CEP inicial" value={r.start} onChange={(e) => setRanges(ranges.map((x, idx) => idx === i ? { ...x, start: e.target.value } : x))} data-testid={`range-start-${i}`} />
            <Input placeholder="CEP final" value={r.end} onChange={(e) => setRanges(ranges.map((x, idx) => idx === i ? { ...x, end: e.target.value } : x))} data-testid={`range-end-${i}`} />
            <button type="button" className="text-slate-400 hover:text-red-500" onClick={() => setRanges(ranges.filter((_, idx) => idx !== i))}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" className="mt-2 gap-1" onClick={() => setRanges([...ranges, { start: "", end: "" }])} data-testid="add-range-button"><Plus className="h-3.5 w-3.5" /> Faixa de CEP</Button>
      </div>
    </div>
  );
}

function CredentialDialog({ tech, open, onOpenChange, onSaved }) {
  const [cities, setCities] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [baseCep, setBaseCep] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && tech) {
      setCities(tech.cities?.length ? tech.cities : [{ city: "", uf: "" }]);
      setRanges(tech.cep_ranges?.length ? tech.cep_ranges : [{ start: "", end: "" }]);
      setBaseCep(tech.base_cep || "");
    }
  }, [open, tech]);

  const submit = async (decision) => {
    setBusy(true);
    const cleanCities = cities.filter((c) => c.city && c.uf);
    const cleanRanges = ranges.filter((r) => r.start && r.end);
    if (decision === "aprovar" && cleanCities.length === 0 && cleanRanges.length === 0) {
      toast.error("Defina ao menos uma cidade ou faixa de CEP para a área do técnico.");
      setBusy(false);
      return;
    }
    try {
      await api.patch(`/technicians/${tech.id}/credential`, { decision, cities: cleanCities, cep_ranges: cleanRanges, base_cep: baseCep });
      toast.success(decision === "aprovar" ? "Técnico credenciado!" : "Credenciamento recusado.");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const isApproved = tech?.credential_status === "aprovado";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="credential-dialog">
        <DialogHeader>
          <DialogTitle>{isApproved ? "Editar área" : "Credenciar"} — {tech?.name}</DialogTitle>
          <DialogDescription>Defina a área de atuação e a localização-base do técnico.</DialogDescription>
        </DialogHeader>
        {tech?.documents?.length > 0 && (
          <div className="rounded-md border border-slate-200 p-2.5 space-y-1.5" data-testid="credential-documents">
            <p className="text-xs font-semibold text-slate-600">Documentos enviados</p>
            {tech.documents.map((d, i) => (
              <a key={i} href={fileUrl(d)} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline" data-testid={`credential-doc-${i}`}>
                <FileText className="h-3.5 w-3.5" /> Documento {i + 1}
              </a>
            ))}
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs">CEP-base (localização do técnico p/ priorizar chamados)</Label>
          <Input value={baseCep} onChange={(e) => setBaseCep(e.target.value)} placeholder="00000-000" data-testid="credential-basecep-input" />
        </div>
        <RegionsEditor cities={cities} setCities={setCities} ranges={ranges} setRanges={setRanges} />
        <DialogFooter className="gap-2">
          {!isApproved && (
            <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 gap-1.5" disabled={busy}
              onClick={() => submit("recusar")} data-testid="credential-reject-button">
              <XCircle className="h-4 w-4" /> Recusar
            </Button>
          )}
          <Button className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" disabled={busy}
            onClick={() => submit("aprovar")} data-testid="credential-approve-button">
            <CheckCircle2 className="h-4 w-4" /> {isApproved ? "Salvar área" : "Aprovar e credenciar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_BADGE = {
  aprovado: { label: "Credenciado", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: ShieldCheck },
  pendente: { label: "Aguardando", cls: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
  recusado: { label: "Recusado", cls: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
};

export default function TechniciansTab() {
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogTech, setDialogTech] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/technicians");
      setTechs(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-slate-400 text-sm">Carregando...</p>;

  const pendentes = techs.filter((t) => t.credential_status === "pendente");
  const outros = techs.filter((t) => t.credential_status !== "pendente");

  const renderCard = (t) => {
    const badge = STATUS_BADGE[t.credential_status] || STATUS_BADGE.aprovado;
    const Icon = badge.icon;
    return (
      <Card key={t.id} className="border-slate-200" data-testid={`technician-card-${t.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            {t.picture ? (
              <img src={fileUrl(t.picture)} alt={t.name} className="h-10 w-10 rounded-full object-cover border" data-testid={`tech-photo-${t.id}`} />
            ) : (
              <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                <Wrench className="h-5 w-5 text-slate-400" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-800 truncate">{t.name}</p>
              <p className="text-xs text-slate-500 truncate">{t.email} · {t.phone || "-"}</p>
            </div>
            <Badge className={`${badge.cls} gap-1`} data-testid={`tech-status-${t.id}`}><Icon className="h-3 w-3" /> {badge.label}</Badge>
          </div>
          {t.avg_rating != null && (
            <div className="flex items-center gap-1 text-sm" data-testid={`tech-rating-${t.id}`}>
              <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
              <span className="font-semibold text-slate-700">{t.avg_rating}</span>
              <span className="text-xs text-slate-400">({t.ratings_count} avaliações)</span>
            </div>
          )}
          {t.documents?.length > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500" data-testid={`tech-docs-${t.id}`}>
              <FileText className="h-3.5 w-3.5" /> {t.documents.length} documento(s) anexado(s)
            </div>
          )}
          {t.credential_note && <p className="text-xs text-slate-500 italic">"{t.credential_note}"</p>}
          {(t.cities?.length > 0) && (
            <div className="flex items-start gap-1.5 text-xs text-slate-600">
              <MapPin className="h-3.5 w-3.5 mt-0.5 text-slate-400" />
              <span>{t.cities.map((c) => `${c.city}/${c.uf}`).join(", ")}</span>
            </div>
          )}
          {(t.cep_ranges?.length > 0) && (
            <div className="flex items-start gap-1.5 text-xs text-slate-600">
              <Hash className="h-3.5 w-3.5 mt-0.5 text-slate-400" />
              <span>{t.cep_ranges.map((r) => `${r.start}–${r.end}`).join(", ")}</span>
            </div>
          )}
          <Button size="sm" variant="outline" className="w-full gap-1.5" onClick={() => setDialogTech(t)}
            data-testid={`credential-button-${t.id}`}>
            {t.credential_status === "pendente" ? "Analisar credenciamento" : "Editar área"}
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="technicians-tab">
      <section>
        <h3 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-amber-600" /> Aguardando credenciamento ({pendentes.length})
        </h3>
        {pendentes.length === 0 ? (
          <p className="text-slate-400 text-sm">Nenhuma solicitação pendente.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="pending-technicians">{pendentes.map(renderCard)}</div>
        )}
      </section>
      <section>
        <h3 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-1.5">
          <ShieldCheck className="h-4 w-4 text-emerald-600" /> Técnicos ({outros.length})
        </h3>
        {outros.length === 0 ? (
          <p className="text-slate-400 text-sm">Nenhum técnico credenciado ainda.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="all-technicians">{outros.map(renderCard)}</div>
        )}
      </section>

      <CredentialDialog tech={dialogTech} open={!!dialogTech}
        onOpenChange={(o) => !o && setDialogTech(null)} onSaved={load} />
    </div>
  );
}
